/* ==========================================================================
   TEAM BRUNE – Theme JavaScript
   Vanilla web components, no dependencies.
   ========================================================================== */
(() => {
  const theme = (window.theme = window.theme || {});
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  const decodeEntities = (str) => {
    const el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  };

  /* ---------- Money ---------- */
  theme.formatMoney = (cents, format) => {
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const fmt = decodeEntities(format || theme.moneyFormat || '{{amount_with_comma_separator}} €');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const delimit = (number, precision, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const [whole, fraction] = (number / 100).toFixed(precision).split('.');
      return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (fraction ? decimal + fraction : '');
    };
    const match = fmt.match(placeholder);
    if (!match) return fmt;
    const formats = {
      amount: () => delimit(value, 2),
      amount_no_decimals: () => delimit(value, 0),
      amount_with_comma_separator: () => delimit(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => delimit(value, 0, '.', ','),
      amount_with_apostrophe_separator: () => delimit(value, 2, "'", '.'),
      amount_with_space_separator: () => delimit(value, 2, ' ', ','),
    };
    return fmt.replace(placeholder, (formats[match[1]] || formats.amount)());
  };

  /* ---------- Dialogs (drawers, modals) ---------- */
  class ThemeDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-dialog-close]')) this.close();
      });
    }

    get isOpen() {
      return Boolean(this.dialog && this.dialog.open);
    }

    open(trigger) {
      if (!this.dialog || this.dialog.open) return;
      this.trigger = trigger || document.activeElement;
      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      const autofocus = this.dialog.querySelector('[data-predictive-input], [data-autofocus]');
      if (autofocus) autofocus.focus();
      this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen || this.dialog.classList.contains('is-closing')) return;
      const finish = () => {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        if (this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
      };
      if (reduceMotion.matches) {
        finish();
        return;
      }
      this.dialog.classList.add('is-closing');
      setTimeout(finish, 320);
    }
  }
  define('theme-dialog', ThemeDialog);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (!opener) return;
    const target = document.getElementById(opener.dataset.dialogOpen);
    if (!target || typeof target.open !== 'function') return;
    event.preventDefault();
    target.open(opener);
  });

  /* ---------- Header ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 12);
      this.onScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.menus = [...this.querySelectorAll('details[data-hover-menu]')];
      this.menus.forEach((menu) => {
        let timer;
        menu.addEventListener('mouseenter', () => {
          if (!canHover.matches) return;
          clearTimeout(timer);
          this.closeMenus(menu);
          menu.open = true;
        });
        menu.addEventListener('mouseleave', () => {
          if (!canHover.matches) return;
          timer = setTimeout(() => (menu.open = false), 160);
        });
        menu.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !menu.open) return;
          menu.open = false;
          menu.querySelector('summary').focus();
        });
        menu.addEventListener('focusout', (event) => {
          if (!menu.contains(event.relatedTarget)) menu.open = false;
        });
      });

      this.onDocClick = (event) => {
        if (!this.contains(event.target)) this.closeMenus();
      };
      document.addEventListener('click', this.onDocClick);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('click', this.onDocClick);
    }

    closeMenus(except) {
      (this.menus || []).forEach((menu) => {
        if (menu !== except) menu.open = false;
      });
    }
  }
  define('site-header', SiteHeader);

  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.messages = [...this.querySelectorAll('.announcement__msg')];
      if (this.messages.length < 2) return;
      this.index = 0;
      this.mobile = window.matchMedia('(max-width: 989px)');
      const pause = () => (this.paused = true);
      const resume = () => (this.paused = false);
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.timer = setInterval(() => {
        if (this.paused || !this.mobile.matches || document.hidden) return;
        this.messages[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.messages.length;
        this.messages[this.index].classList.add('is-active');
      }, parseInt(this.dataset.interval, 10) || 5000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  define('announcement-rotator', AnnouncementRotator);

  /* ---------- Cart ---------- */
  const cart = {
    sectionIds() {
      const ids = [];
      if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
      document.querySelectorAll('cart-items[data-section]').forEach((el) => ids.push(el.dataset.section));
      return ids;
    },

    async add(formData) {
      const ids = this.sectionIds();
      if (ids.length) {
        formData.append('sections', ids.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.cartError);
      }
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      return data;
    },

    async change(line, quantity) {
      const response = await fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          line,
          quantity,
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.cartError);
      }
      this.render(data.sections);
      return data;
    },

    render(sections) {
      if (!sections) return;
      const active = document.activeElement;
      const focusKey = active && active.closest('cart-items') ? this.focusKey(active) : null;

      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-drawer') {
          const fresh = doc.querySelector('[data-cart-drawer-inner]');
          const current = document.querySelector('#CartDrawer [data-cart-drawer-inner]');
          if (!fresh || !current) return;
          current.innerHTML = fresh.innerHTML;
          current.dataset.cartCountValue = fresh.dataset.cartCountValue;
          this.updateCount(parseInt(fresh.dataset.cartCountValue, 10) || 0);
        } else {
          const fresh = doc.getElementById(`shopify-section-${id}`);
          const current = document.getElementById(`shopify-section-${id}`);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        }
      });

      if (focusKey) {
        const next = document.querySelector(focusKey);
        const fallback = document.querySelector('#CartDrawer dialog[open] [data-dialog-close]');
        (next || fallback || document.body).focus();
      }
    },

    focusKey(el) {
      const line = el.closest('[data-line]');
      if (!line) return null;
      if (el.dataset.step) return `[data-line="${line.dataset.line}"] [data-step="${el.dataset.step}"]`;
      if (el.classList.contains('qty__input')) return `[data-line="${line.dataset.line}"] .qty__input`;
      return null;
    },

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.hidden = count === 0;
        const visible = badge.querySelector('[aria-hidden]');
        if (visible) visible.textContent = count;
        const spoken = badge.querySelector('.visually-hidden');
        if (spoken) spoken.textContent = spoken.textContent.replace(/\d+/, count);
        badge.classList.remove('is-bumped');
        void badge.offsetWidth;
        badge.classList.add('is-bumped');
      });
    },

    open(trigger) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(trigger);
        return true;
      }
      window.location.href = theme.routes.cart;
      return false;
    },
  };
  theme.cart = cart;

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.pending = {};
      this.addEventListener('change', (event) => {
        const input = event.target.closest('.qty__input');
        if (!input) return;
        const line = parseInt(input.dataset.line, 10);
        clearTimeout(this.pending[line]);
        this.pending[line] = setTimeout(() => this.update(line, Math.max(0, parseInt(input.value, 10) || 0)), 350);
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove-line]');
        if (!remove) return;
        event.preventDefault();
        this.update(parseInt(remove.dataset.removeLine, 10), 0);
      });
    }

    async update(line, quantity) {
      this.classList.add('is-loading');
      const errorEl = this.querySelector('[data-cart-error]');
      try {
        await cart.change(line, quantity);
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  define('cart-items', CartItems);

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-step]');
        if (!button || !this.input) return;
        const min = parseInt(this.input.min, 10) || 0;
        const current = parseInt(this.input.value, 10) || 0;
        const next = Math.max(min, current + parseInt(button.dataset.step, 10));
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  define('quantity-input', QuantityInput);

  /* ---------- Quick add on product cards ---------- */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.card__quick-toggle');
      this.status = this.querySelector('.card__quick-status');
      if (this.toggle) {
        this.toggle.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')));
      }
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-variant-id]');
        if (button && !button.disabled) this.add(button);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;
        this.setOpen(false);
        this.toggle.focus();
      });
    }

    setOpen(open) {
      if (open) {
        document.querySelectorAll('quick-add.is-open').forEach((other) => other !== this && other.setOpen(false));
      }
      this.classList.toggle('is-open', open);
      if (this.toggle) this.toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        const first = this.querySelector('.chip:not(:disabled)');
        if (first) first.focus({ preventScroll: true });
      }
    }

    async add(button) {
      const formData = new FormData();
      formData.append('id', button.dataset.variantId);
      formData.append('quantity', '1');
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      if (this.status) {
        this.status.classList.add('visually-hidden');
        this.status.textContent = theme.strings.adding;
      }
      try {
        await cart.add(formData);
        button.classList.add('is-added');
        if (this.status) this.status.textContent = theme.strings.added;
        setTimeout(() => button.classList.remove('is-added'), 1600);
        this.setOpen(false);
        if (theme.cartType === 'drawer') cart.open(this.toggle || button);
        else window.location.href = theme.routes.cart;
      } catch (error) {
        if (this.status) {
          this.status.textContent = error.message;
          this.status.classList.remove('visually-hidden');
          this.status.classList.add('card__quick-error');
        }
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
  }
  define('quick-add', QuickAdd);

  document.addEventListener('click', (event) => {
    document.querySelectorAll('quick-add.is-open').forEach((el) => {
      if (!el.contains(event.target)) el.setOpen(false);
    });
  });

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.results = this.querySelector('[data-predictive-results]');
      const dialog = this.closest('dialog');
      this.input = dialog ? dialog.querySelector('[data-predictive-input]') : null;
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', debounce(() => this.search(), 260));
    }

    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: this.dataset.section || 'predictive-search',
      });
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.statusText);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const inner = doc.querySelector('.predictive__inner');
        this.results.innerHTML = inner ? inner.outerHTML : '';
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  define('predictive-search', PredictiveSearch);

  /* ---------- Hero parallax ---------- */
  class ParallaxMedia extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-parallax') || reduceMotion.matches) return;
      const host = this.parentElement;
      let ticking = false;
      const update = () => {
        const rect = host.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          const offset = Math.max(0, -rect.top) * 0.28;
          this.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
        }
        ticking = false;
      };
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  define('parallax-media', ParallaxMedia);

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-video-toggle]');
    if (!toggle) return;
    const video = toggle.closest('.hero').querySelector('video');
    if (!video) return;
    if (video.paused) {
      video.play();
      toggle.setAttribute('aria-pressed', 'false');
    } else {
      video.pause();
      toggle.setAttribute('aria-pressed', 'true');
    }
  });

  /* ---------- Stats count-up ---------- */
  class CountUp extends HTMLElement {
    connectedCallback() {
      const target = parseInt(this.dataset.target, 10);
      if (Number.isNaN(target) || target === 0 || reduceMotion.matches || designMode()) return;
      if (!('IntersectionObserver' in window)) return;
      const rect = this.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) return;
      this.textContent = '0';
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const duration = 1400;
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          this.textContent = Math.round(target * (1 - Math.pow(1 - progress, 3)));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, { threshold: 0.6 });
      observer.observe(this);
    }
  }
  define('count-up', CountUp);

  /* ---------- Crew pass tilt ---------- */
  class CrewPass extends HTMLElement {
    connectedCallback() {
      if (reduceMotion.matches || !canHover.matches) return;
      const zone = this.closest('.crew__pass-wrap') || this;
      zone.addEventListener('pointermove', (event) => {
        const rect = this.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        this.classList.add('is-tilting');
        this.style.setProperty('--ry', `${(x * 18).toFixed(2)}deg`);
        this.style.setProperty('--rx', `${(-y * 12).toFixed(2)}deg`);
        this.style.setProperty('--foil', `${((x + 0.5) * 100).toFixed(0)}%`);
      });
      zone.addEventListener('pointerleave', () => {
        this.classList.remove('is-tilting');
        this.style.setProperty('--rx', '0deg');
        this.style.setProperty('--ry', '0deg');
        this.style.setProperty('--foil', '100%');
      });
    }
  }
  define('crew-pass', CrewPass);

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;
  theme.observeReveal = (root = document) => {
    if (!revealObserver) return;
    root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => revealObserver.observe(el));
  };

  const initReveal = () => {
    const enabled = document.body.classList.contains('has-reveal');
    if (!enabled || reduceMotion.matches || designMode() || !('IntersectionObserver' in window)) return;
    const elements = [...document.querySelectorAll('[data-reveal]')];
    const viewport = window.innerHeight;
    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < viewport && rect.bottom > 0) el.classList.add('is-visible');
    });
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.1 });
    document.documentElement.classList.add('reveal-ready');
    theme.observeReveal();
  };

  /* ---------- Misc ---------- */
  document.addEventListener('change', (event) => {
    const form = event.target.closest('form[data-autosubmit]');
    if (form) form.submit();
  });

  if (reduceMotion.matches) {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('.hero video[autoplay]').forEach((video) => {
        video.pause();
        const toggle = video.closest('.hero').querySelector('[data-video-toggle]');
        if (toggle) toggle.setAttribute('aria-pressed', 'true');
      });
    });
  }

  document.addEventListener('shopify:section:load', (event) => theme.observeReveal(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }
})();
